import { supabase } from '../lib/supabase';

/**
 * Schema cache to store table column information
 * Reduces repeated database queries
 */
const schemaCache = {};

/**
 * Fetches and caches the column names for a given table
 * @param {string} tableName - Name of the database table
 * @returns {Promise<string[]>} Array of column names
 */
async function getTableColumns(tableName) {
  // Return cached schema if available
  if (schemaCache?.[tableName]) {
    return schemaCache?.[tableName];
  }

  try {
    // First try: Query with limit(0) to get column structure without data
    // This works even if the table is empty
    let { data, error } = await supabase
      ?.from(tableName)
      ?.select('*')
      ?.limit(0);

    // If limit(0) doesn't return structure, try limit(1)
    if (!data || (Array.isArray(data) && data?.length === 0)) {
      const result = await supabase
        ?.from(tableName)
        ?.select('*')
        ?.limit(1);
      
      data = result?.data;
      error = result?.error;
    }

    if (error) {
      console.error(`Schema fetch error for ${tableName}:`, error);
      // Return empty array instead of throwing to allow operation to continue
      return [];
    }

    // Extract column names from the response
    // For limit(0), Supabase may return an empty array but we can still get columns from metadata
    let columns = [];
    
    if (data && Array.isArray(data)) {
      if (data?.length > 0) {
        columns = Object.keys(data?.[0]);
      } else {
        // Table is empty or limit(0) returned no data
        // Use a fallback: make a HEAD request or return common columns
        console.warn(`Table ${tableName} returned no data for schema detection`);
        // Return empty to skip validation
        return [];
      }
    }
    
    // Cache the schema
    schemaCache[tableName] = columns;
    
    return columns;
  } catch (err) {
    console.error(`Failed to fetch schema for ${tableName}:`, err);
    // Return empty array to allow operation to continue without validation
    return [];
  }
}

/**
 * Validates that all fields in the data object exist in the target table
 * @param {string} tableName - Name of the database table
 * @param {Object} data - Data object to validate
 * @returns {Promise<{valid: boolean, invalidFields: string[], validData: Object}>}
 */
export async function validateSchema(tableName, data) {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      invalidFields: [],
      validData: {},
      error: 'Data must be a valid object'
    };
  }

  // Get table columns
  let columns = await getTableColumns(tableName);
  
  if (columns?.length === 0) {
    console.warn(`No schema found for table: ${tableName}`);
    return {
      valid: true,
      invalidFields: [],
      validData: data,
      warning: `Schema validation skipped - no columns found for ${tableName}`
    };
  }

  // Check each field in data against schema
  const dataFields = Object.keys(data);
  const invalidFields = dataFields?.filter(field => !columns?.includes(field));
  
  // Create valid data object with only matching fields
  const validData = {};
  dataFields?.forEach(field => {
    if (columns?.includes(field)) {
      validData[field] = data?.[field];
    }
  });

  const isValid = invalidFields?.length === 0;

  if (!isValid) {
    console.error(`Schema validation failed for table "${tableName}":`);
    console.error(`Invalid fields: ${invalidFields?.join(', ')}`);
    console.error(`Valid columns: ${columns?.join(', ')}`);
  }

  return {
    valid: isValid,
    invalidFields,
    validData,
    availableColumns: columns
  };
}

/**
 * Validates and throws an error if schema validation fails
 * Use this for strict validation where you want to prevent the operation
 * @param {string} tableName - Name of the database table
 * @param {Object} data - Data object to validate
 * @throws {Error} If validation fails
 */
export async function validateSchemaStrict(tableName, data) {
  try {
    const result = await validateSchema(tableName, data);
    
    // If schema fetch failed (no columns found), skip validation and return original data
    if (result?.warning || (result?.availableColumns && result?.availableColumns?.length === 0)) {
      console.warn(`Schema validation skipped for ${tableName}, proceeding with original data`);
      return data;
    }
    
    if (!result?.valid) {
      const errorMessage = `Schema validation error for table "${tableName}":\n` +
        `Invalid fields: ${result?.invalidFields?.join(', ')}\n` +
        `Available columns: ${result?.availableColumns?.join(', ')}\n\n` +
        `Please update your code to use the correct column names.`;
      
      throw new Error(errorMessage);
    }
    
    return result?.validData;
  } catch (error) {
    // If validation itself fails (e.g., network error), log and return original data
    console.error(`Schema validation failed for ${tableName}:`, error);
    console.warn('Proceeding with unvalidated data');
    return data;
  }
}

/**
 * Clears the schema cache for a specific table or all tables
 * Useful when schema changes are made
 * @param {string} [tableName] - Optional table name to clear, clears all if not provided
 */
export function clearSchemaCache(tableName = null) {
  if (tableName) {
    delete schemaCache?.[tableName];
    console.log(`Schema cache cleared for: ${tableName}`);
  } else {
    Object.keys(schemaCache)?.forEach(key => delete schemaCache?.[key]);
    console.log('All schema cache cleared');
  }
}

/**
 * Gets the current schema cache (for debugging)
 * @returns {Object} Current schema cache
 */
export function getSchemaCache() {
  return { ...schemaCache };
}

export default {
  validateSchema,
  validateSchemaStrict,
  clearSchemaCache,
  getSchemaCache
};